import { zodResolver } from '@hookform/resolvers/zod';
import AUS from 'public:images/flags/AUS.svg';
import DEU from 'public:images/flags/DEU.svg';
import GBR from 'public:images/flags/GBR.svg';
import ITA from 'public:images/flags/ITA.svg';
import USA from 'public:images/flags/USA.svg';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';
import { Form } from 'softcat:components/form.tsx';
import { Spinner } from 'softcat:components/spinner.tsx';
import { AccountType } from 'softcat:config/account-type.ts';
import { AuthConfig } from 'softcat:config/auth.ts';
import { Territory } from 'softcat:config/territory.ts';
import { UserRole } from 'softcat:config/user-role.ts';
import { useAuth } from 'softcat:hooks/use-auth.ts';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { UserService } from 'softcat:services/user.ts';
import useSWR from 'swr';
import * as z from 'zod';

export const UsersEdit = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { activeTerritory } = useTerritory();
    const { user, refreshUser } = useAuth();
    const { id = '' } = useParams<{ id: string }>();
    const [submitting, setSubmitting] = useState(false);
    const [onEditError, setOnEditError] = useState('');
    const [formValues, setFormValues] = useState({
        email: '',
        enabled: true,
        password: '',
        role: UserRole.UPLOADER,
        territories: [] as Territory[],
    });

    const EditUserSchema = z.object({
        email: z.string().refine(
            (value) => {
                return value === '' || z.string().email().safeParse(value).success;
            },
            {
                message: intl.formatMessage({ id: 'USERS_FORM_EMAIL_VALIDATION' }),
            },
        ),
        enabled: z.boolean(),
        password: z
            .string()
            .optional()
            .refine(
                /* v8 ignore next 3 */
                (value) => {
                    return !value || /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,16}$/.test(value);
                },
                {
                    message: intl.formatMessage({ id: 'USERS_FORM_PASSWORD_VALIDATION_ALPHANUMERIC' }),
                },
            ),
        role: z.nativeEnum(UserRole),
        territories: z.array(z.nativeEnum(Territory)).min(1, { message: intl.formatMessage({ id: 'USERS_FORM_TERRITORIES_VALIDATION' }) }),
    });

    const {
        data: userData,
        error: userDataError,
        isLoading: userDataIsLoading,
    } = useSWR({ id, key: `${activeTerritory}-UserServiceFindOne` }, UserService.findOne);

    const extractHighestRole = useCallback(() => {
        const rolesHierarchy = Object.entries(AuthConfig.RoleHierarchy)
            .sort(([, roleOrderValueA], [, roleOrderValueB]) => {
                return roleOrderValueB - roleOrderValueA;
            })
            .map(([role]) => {
                return role;
            });
        const userRoles = userData?.roles || [];

        for (const role of rolesHierarchy) {
            if (userRoles.includes(role)) {
                return role as UserRole;
            }
        }

        return UserRole.UPLOADER;
    }, [userData]);

    const territoryOptions = useMemo(() => {
        return [
            { flag: AUS, value: Territory.AUS },
            { flag: DEU, value: Territory.DEU },
            { flag: GBR, value: Territory.GBR },
            { flag: ITA, value: Territory.ITA },
            { flag: USA, value: Territory.USA },
        ];
    }, []);

    useEffect(() => {
        if (userData) {
            setFormValues({
                email: userData.email || '',
                enabled: userData.enabled,
                password: '',
                role: extractHighestRole(),
                territories: userData.territories as Territory[],
            });
        }
    }, [extractHighestRole, userData]);

    const onSubmit = async ({ role, ...values }: z.infer<typeof EditUserSchema>) => {
        setSubmitting(true);
        setOnEditError('');

        if (userData) {
            try {
                await UserService.update({
                    ...userData,
                    email: values.email,
                    enabled: values.enabled,
                    password: values.password || undefined,
                    roles: [role],
                    territories: values.territories,
                });

                if (user?.id === userData.id) {
                    await refreshUser();
                }

                setSubmitting(false);

                return navigate('/users');
            } catch (error) {
                setSubmitting(false);
                setOnEditError((error as Error).message);
            }
        }
    };

    return (
        <Form<z.infer<typeof EditUserSchema>> defaultValues={formValues} onSubmit={onSubmit} resolver={zodResolver(EditUserSchema)}>
            <div className="space-y-3 px-4">
                <Form.Header
                    description={<FormattedMessage id={'USERS_EDIT_PAGE_DESCRIPTION'} />}
                    title={<FormattedMessage id={'USERS_EDIT_PAGE_HEADING'} />}
                />

                {userDataError && (
                    <p className="mt-2 text-sm text-gray-700">
                        <FormattedMessage id={'USERS_EDIT_PAGE_ERROR_LOADING_DATA'} />
                    </p>
                )}

                {userDataIsLoading && <Spinner />}

                {userData && (
                    <>
                        <Form.StaticValue label={<FormattedMessage id={'USERS_FORM_ACCOUNT_TYPE_LABEL'} />} value={userData.accountType} />

                        <Form.StaticValue label={<FormattedMessage id={'USERS_FORM_USERNAME_LABEL'} />} value={userData.username} />

                        {userData.accountType === AccountType.LOCAL && (
                            <Form.Input
                                disabled
                                label={<FormattedMessage id={'USERS_FORM_PASSWORD_LABEL'} />}
                                name={'password'}
                                placeholder={'••••••••••'}
                                type={'password'}
                            />
                        )}

                        <Form.Input disabled label={<FormattedMessage id={'USERS_FORM_EMAIL_LABEL'} />} name={'email'} />

                        {user?.id === userData.id ? (
                            <Form.StaticValue
                                label={<FormattedMessage id={'USERS_FORM_ROLE_LABEL'} />}
                                value={`${extractHighestRole()} (${intl.formatMessage({ id: 'USERS_EDIT_PAGE_ROLE_CONDITION' })})`}
                            />
                        ) : (
                            <Form.RadioGroup
                                label={<FormattedMessage id={'USERS_FORM_ROLE_LABEL'} />}
                                name={'role'}
                                options={[
                                    { label: UserRole.SUPERADMIN, value: UserRole.SUPERADMIN },
                                    { label: UserRole.ADMIN, value: UserRole.ADMIN },
                                    { label: UserRole.USER, value: UserRole.USER },
                                    { label: UserRole.UPLOADER, value: UserRole.UPLOADER },
                                ]}
                            />
                        )}

                        <Form.Checkbox disabled={user?.id === userData.id} label={<FormattedMessage id={'USERS_FORM_ENABLED_LABEL'} />} name={'enabled'} />

                        <Form.Checkbox
                            label={<FormattedMessage id={'USERS_FORM_TERRITORIES_LABEL'} />}
                            name={'territories'}
                            options={territoryOptions.map((territory) => {
                                return {
                                    disabled: user?.id === userData.id && territory.value === activeTerritory,
                                    label: (
                                        <div className={'ml-2 flex items-center gap-1.5'}>
                                            <img alt={territory.value} className="inline-block size-5" src={territory.flag} />
                                            <span className={'text-sm'}>{territory.value}</span>
                                        </div>
                                    ),
                                    value: territory.value,
                                };
                            })}
                        />

                        <Form.Footer
                            isSubmitting={submitting}
                            onCancel={() => {
                                return navigate('/users');
                            }}
                            submitError={onEditError && <FormattedMessage id={'USERS_EDIT_PAGE_FORM_SUBMIT_ERROR'} values={{ error: onEditError }} />}
                            submitText={<FormattedMessage id={'BUTTON_UPDATE'} />}
                        />
                    </>
                )}
            </div>
        </Form>
    );
};
